import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { DeleteIcon } from '@chakra-ui/icons';
import { Box, Button, Checkbox, HStack, Input, Text, Textarea, VStack } from '@chakra-ui/react';

const KycRfiQuestion = ({
  index,
  question: {
    prompt,
    transaction_id,
    is_document_required,
    is_front_back_required,
    send_kyc_link,
    document_sub_type,
    document_type,
    rfi_tag_type,
    rfi_tag_subtype,
  },
  updateQuestion,
  handleDeleteQuestion,
  isLastQuestion,
  documentInfo,
  updateMultipleQuestions,
  resourceType,
  rfiTagTypesOptions,
  rfiTagSubtypesOptions,
}) => {
  const questionRef = useRef(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [subtypes, setSubtypes] = useState([]);
  const [selectedSubtype, setSelectedSubtype] = useState([]);
  const [otherSubtype, setOtherSubtype] = useState('');
  const [selectedRfiTagType, setSelectedRfiTagType] = useState();
  const [selectedRfiTagSubtype, setSelectedRfiTagSubtype] = useState();
  const [rfiTagSubtypes, setRfiTagSubtypes] = useState([]);
  const [otherRfiTagSubtype, setOtherRfiTagSubtype] = useState('');

  useEffect(() => {
    if (isLastQuestion) {
      questionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [isLastQuestion]);

  const handleDocumentTypeChange = (selectedOption) => {
    setSelectedDocumentType(selectedOption);
    updateQuestion(index, 'document_type', selectedOption ? selectedOption.value : '');

    const document = documentInfo.find((doc) => doc.value === selectedOption?.value);

    const subtypeOptions = document ? [...document.subtypes, { value: 'other', label: 'Other' }] : [];

    setSubtypes(subtypeOptions);
    setSelectedSubtype([]);
    setOtherSubtype('');
  };

  const handleSubtypeChange = (selectedOptions) => {
    const selectedValues = selectedOptions ? selectedOptions.map((option) => option.value) : [];

    setSelectedSubtype(selectedOptions);

    if (selectedValues.includes('other')) {
      setOtherSubtype('');
    }
    updateQuestion(index, 'document_sub_type', selectedValues);
  };

  const handleOtherSubtypeChange = (e) => {
    const value = e.target.value;

    setOtherSubtype(value);
    updateQuestion(index, 'document_sub_type_other', value);
  };

  const handleIsDocumentRequiredChange = () => {
    setSelectedDocumentType(null);
    setSubtypes([]);
    setSelectedSubtype([]);
    setOtherSubtype('');
    updateQuestion(index, 'is_document_required', !is_document_required);
  };

  const handleSendKycLinkChange = () => {
    updateMultipleQuestions(index, {
      send_kyc_link: !send_kyc_link,
      is_document_required: !send_kyc_link ? false : is_document_required,
      is_front_back_required: !send_kyc_link ? false : is_front_back_required,
      document_type: '',
      document_sub_type: [],
      document_sub_type_other: '',
    });
    setSelectedDocumentType(null);
    setSubtypes([]);
    setSelectedSubtype([]);
    setOtherSubtype('');
  };

  const handleRfiTagTypeChange = (selectedOption) => {
    setSelectedRfiTagType(selectedOption);
    updateQuestion(index, 'rfi_tag_type', selectedOption ? selectedOption.value : '');

    setRfiTagSubtypes(rfiTagSubtypesOptions[selectedOption?.value] ?? []);
    setSelectedRfiTagSubtype();
  };

  const handleRfiTagSubtypeChange = (selectedOption) => {
    setSelectedRfiTagSubtype(selectedOption);
    updateQuestion(index, 'rfi_tag_subtype', selectedOption ? selectedOption.value : '');
  };

  const handleOtherRfiTagSubtypeChange = (e) => {
    const value = e.target.value;

    setOtherRfiTagSubtype(value);
    updateQuestion(index, 'rfi_tag_type_other', value);
  };

  const selectStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, backgroundColor: 'white', minWidth: '200px' }),
    }),
    []
  );

  useEffect(() => {
    const document = documentInfo.find((doc) => doc.value === document_type);
    const subtypeOptions = document ? [...document.subtypes, { value: 'other', label: 'Other' }] : [];
    const selectedSubtypeOptions = subtypeOptions.filter((subtype) => document_sub_type.includes(subtype.value));

    setSelectedDocumentType({ label: document?.label, value: document?.value });
    setSubtypes(subtypeOptions);
    setSelectedSubtype(selectedSubtypeOptions);
  }, [document_type, document_sub_type]);

  useEffect(() => {
    setSelectedRfiTagType(rfiTagTypesOptions?.find((type) => type.value === rfi_tag_type));
    setSelectedRfiTagSubtype(rfiTagSubtypesOptions[rfi_tag_type]?.find((subtype) => subtype.value === rfi_tag_subtype));
  }, [rfi_tag_type, rfi_tag_subtype]);

  return (
    <Box
      ref={questionRef}
      border='1px solid'
      borderColor='gray.200'
      p={4}
      borderRadius='md'
      background='white'
      w='full'
    >
      <HStack gap={4} alignItems='start'>
        <VStack flex='1' align='stretch' spacing={4}>
          <Text fontWeight='bold'>Question {index + 1}</Text>
          <Textarea
            placeholder='Enter question'
            value={prompt}
            onChange={(e) => updateQuestion(index, 'prompt', e.target.value)}
          />
          <Input
            placeholder='Transaction ID (optional)'
            value={transaction_id}
            onChange={(e) => updateQuestion(index, 'transaction_id', e.target.value)}
          />
          <HStack>
            <Select
              placeholder='Select RFI Tag Type (optional)'
              options={rfiTagTypesOptions}
              value={selectedRfiTagType}
              onChange={handleRfiTagTypeChange}
              isClearable
              styles={selectStyles}
              menuPosition='fixed'
            />
            {rfiTagSubtypesOptions[selectedRfiTagType?.value] && (
              <Select
                placeholder='Select RFI Tag Subtype'
                options={rfiTagSubtypes}
                value={selectedRfiTagSubtype}
                onChange={handleRfiTagSubtypeChange}
                isClearable
                styles={selectStyles}
                menuPosition='fixed'
              />
            )}
            {selectedRfiTagType?.value === 'others' && (
              <Input
                placeholder='Enter other subtype'
                maxLength={1000}
                value={otherRfiTagSubtype}
                onChange={handleOtherRfiTagSubtypeChange}
                backgroundColor='white'
              />
            )}
          </HStack>
        </VStack>

        <VStack flex='1' align='stretch' spacing={4}>
          <HStack justifyContent='space-between' alignContent='center'>
            <Checkbox isChecked={send_kyc_link} onChange={handleSendKycLinkChange}>
              Send {resourceType} Link
            </Checkbox>
            <Checkbox
              isChecked={is_document_required}
              onChange={handleIsDocumentRequiredChange}
              isDisabled={send_kyc_link}
            >
              Document Required
            </Checkbox>
            <Checkbox
              isChecked={is_front_back_required}
              onChange={() => updateQuestion(index, 'is_front_back_required', !is_front_back_required)}
              isDisabled={!is_document_required}
            >
              Both-Side Required
            </Checkbox>
            <Button
              onClick={() => handleDeleteQuestion(index)}
              variant='outline'
              colorScheme='red'
              size='sm'
              disabled={index === 0}
            >
              <DeleteIcon color='red.500' />
            </Button>
          </HStack>

          {is_document_required && (
            <>
              <Select
                placeholder='Select Document Type'
                options={documentInfo.map((doc) => ({ value: doc.value, label: doc.label }))}
                value={selectedDocumentType}
                onChange={handleDocumentTypeChange}
                isClearable
                styles={selectStyles}
                menuPosition='fixed'
              />

              <Select
                isMulti
                placeholder='Select Subtype'
                options={subtypes}
                value={selectedSubtype}
                onChange={handleSubtypeChange}
                styles={selectStyles}
                menuPosition='fixed'
                isDisabled={!selectedDocumentType}
              />

              {selectedSubtype.some((subtype) => subtype.value === 'other') && (
                <Textarea
                  placeholder='Enter other subtype'
                  maxLength={1000}
                  value={otherSubtype}
                  onChange={handleOtherSubtypeChange}
                  backgroundColor='white'
                />
              )}
            </>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

export default KycRfiQuestion;
